import React from 'react';
import { RewardHistoryItem, RewardHistoryFilter } from '../types/RewardHistory';
import StablePopoverTextFilter from '../../../components/customs/table-header/stable-popover-text-filter';
import PopoverSingleSelectFilter from '../../../components/customs/table-header/popover-single-select-filter';
import DefaultAvatar from '../../../components/default-avatar';

interface RewardHistoryTableProps {
    data: RewardHistoryItem[];
    loading?: boolean;
    onResend: (item: RewardHistoryItem) => void;
    filter: RewardHistoryFilter;
    setFilter: (filter: RewardHistoryFilter) => void;
}

const RewardHistoryTable: React.FC<RewardHistoryTableProps> = ({
    data,
    loading = false,
    onResend,
    filter,
    setFilter
}) => {
    // State to manage popover open/close for each filter
    const [filterPopoverStates, setFilterPopoverStates] = React.useState<{[key: string]: boolean}>({
        userSearch: false,
        rewardSearch: false,
        status: false
    });

    // Create wrapper functions to match the expected type signature
    const handleFilterChange = (newFilter: RewardHistoryFilter | ((prev: RewardHistoryFilter) => RewardHistoryFilter)) => {
        if (typeof newFilter === 'function') {
            setFilter(newFilter(filter));
        } else {
            setFilter(newFilter);
        }
    };

    // Handle popover open/close
    const handlePopoverChange = (filterKey: string, isOpen: boolean) => {
        setFilterPopoverStates(prev => ({
            ...prev,
            [filterKey]: isOpen
        }));
    };

    // Helper function to check if a filter is active
    const hasActiveFilter = (filterKey: keyof RewardHistoryFilter) => {
        const value = filter[filterKey];
        if (typeof value === 'string') {
            return value.trim() !== '';
        }
        return false;
    };

    // Helper function to get TableHead classes
    const getTableHeadClasses = (filterKey?: keyof RewardHistoryFilter) => {
        const baseClasses = "relative transition-colors duration-200";
        return baseClasses;
    };

    // Helper function to get text color based on filter state
    const getTableHeadTextClasses = (filterKey?: keyof RewardHistoryFilter) => {
        if (filterKey && hasActiveFilter(filterKey)) {
            return "text-blue-700 dark:text-blue-300 font-medium";
        }
        return "";
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Redeemed':
                return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
            case 'Sent':
                return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
            case 'Pending':
                return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300';
            case 'Failed':
                return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
            default:
                return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300';
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="w-full font-nunito">
            <table className="w-full border-collapse">
                <thead>
                    <tr className="border-b">
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${getTableHeadClasses()}`}>Image</th>
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${getTableHeadClasses('userSearch')}`}>
                            <div className="flex items-center">
                                <span className={getTableHeadTextClasses('userSearch')}>
                                    User
                                </span>
                                <StablePopoverTextFilter
                                    filterState={{
                                        filterKey: 'userSearch',
                                        filter: filter,
                                        setFilter: handleFilterChange
                                    }}
                                    placeholder="Search by user name..."
                                    title="FILTER BY USER"
                                    description="Search by user name only"
                                    isOpen={filterPopoverStates.userSearch}
                                    onOpenChange={(open) => handlePopoverChange('userSearch', open)}
                                />
                            </div>
                        </th>
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${getTableHeadClasses('rewardSearch')}`}>
                            <div className="flex items-center">
                                <span className={getTableHeadTextClasses('rewardSearch')}>
                                    Reward Name
                                </span>
                                <StablePopoverTextFilter
                                    filterState={{
                                        filterKey: 'rewardSearch',
                                        filter: filter,
                                        setFilter: handleFilterChange
                                    }}
                                    placeholder="Search by reward name..."
                                    title="FILTER BY REWARD NAME"
                                    description="Search by reward name"
                                    isOpen={filterPopoverStates.rewardSearch}
                                    onOpenChange={(open) => handlePopoverChange('rewardSearch', open)}
                                />
                            </div>
                        </th>
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${getTableHeadClasses()}`}>Date Redeemed</th>
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${getTableHeadClasses()}`}>Points Used</th>
                        <th className={`h-12 px-4 text-center align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${getTableHeadClasses('status')}`}>
                            <div className="flex items-center justify-center">
                                <span className={getTableHeadTextClasses('status')}>
                                    Redemption Status
                                </span>
                                <PopoverSingleSelectFilter
                                    filterState={{
                                        filterKey: 'status',
                                        filter: filter,
                                        setFilter: handleFilterChange
                                    }}
                                    options={[
                                        { value: 'all', label: 'All Status' },
                                        { value: 'redeemed', label: 'Redeemed' },
                                        { value: 'used', label: 'Sent' },
                                        { value: 'pending', label: 'Pending' }
                                    ]}
                                    placeholder="Select status"
                                    title="FILTER BY STATUS"
                                    isOpen={filterPopoverStates.status}
                                    onOpenChange={(open) => handlePopoverChange('status', open)}
                                />
                            </div>
                        </th>
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${getTableHeadClasses()}`}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((item) => {
                        return (
                        <tr key={item.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="py-4 px-2">
                                {item.user?.avatar ? (
                                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                        <img src={item.user.avatar} alt={item.user?.name || ''} className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80" />
                                    </div>
                                ) : (
                                    <DefaultAvatar size={40} />
                                )}
                            </td>
                            <td className="py-4 px-2">
                                <div>
                                    <div className="font-semibold text-gray-900 dark:text-gray-100 font-nunito text-sm">{item.user?.name || ''}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 font-nunito">{item.user?.email || ''}</div>
                                </div>
                            </td>
                            <td className="py-4 px-2">
                                <div className="font-semibold text-gray-900 dark:text-gray-100 font-nunito text-sm">{item.reward?.name || ''}</div>
                            </td>
                            <td className="py-4 px-2 text-gray-600 dark:text-gray-300 font-nunito text-sm">{item.dateRedeemed || ''}</td>
                            <td className="py-4 px-2 text-gray-600 dark:text-gray-300 font-nunito font-bold text-sm">{item.pointsUsed || 0} pts</td>
                            <td className="py-4 px-2 flex items-center justify-center min-h-[73px]">
                                <p className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold font-nunito ${getStatusColor(item.status || '')}`}>
                                    {item.status || ''}
                                </p>
                            </td>
                            <td className="py-4 px-2">
                                <div className="flex flex-col space-y-1">
                                    <span 
                                        className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold font-nunito transition-colors w-fit ${
                                            item.actions?.canResend 
                                                ? 'text-white bg-[#00C29B] hover:bg-[#00B08A] cursor-pointer' 
                                                : 'text-[#AFAFAF] bg-[#F5F5F5] dark:bg-gray-700 dark:text-gray-400'
                                        }`}
                                        onClick={() => item.actions?.canResend && onResend(item)}
                                    >
                                        Resend
                                    </span>
                                    {item.actions?.lastResent && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 font-nunito">
                                            Resent on {item.actions.lastResent}
                                        </span>
                                    )}
                                </div>
                            </td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default RewardHistoryTable;
